import React, { useState } from "react";
import {
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaWhatsapp,
  FaFacebookF,
} from "react-icons/fa";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import logoImg from "../assets/img/WebLogo2.png";

const Footer = () => {
  const [email, setEmail] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Newsletter subscription email:", email);
    setEmail("");
  };

  const getCurrentYear = () => new Date().getFullYear();

  const footerLinks = {
    company: [
      { name: "Home", path: "/" },
      { name: "About Us", path: "/aboutOethical" },
      { name: "Services", path: "/#services" },
      { name: "Projects", path: "/project" },
      { name: "Team", path: "/team" },
    ],
    services: [
      { name: "Web Development", path: "/#services" },
      { name: "Mobile App Development", path: "/#services" },
      { name: "Digital Marketing", path: "/#services" },
      { name: "SEO Services", path: "/#services" },
      { name: "IT Support", path: "/#services" },
    ],
    legal: [
      { name: "Terms of Service", path: "/termAndCondition" },
      { name: "Privacy Policy", path: "/privacyPolicy" },
      { name: "Careers", path: "/career" },
      { name: "Contact", path: "/contactUs" },
    ],
  };

  const socialLinks = [
    { icon: FaWhatsapp, href: "https://wa.me/919532912517", label: "WhatsApp" },
    { icon: FaTwitter, href: "https://x.com/oethical_tech", label: "Twitter" },
    { icon: FaInstagram, href: "https://www.instagram.com/oethicaltechnology/", label: "Instagram" },
    { icon: FaLinkedinIn, href: "https://www.linkedin.com/company/oethical-technology/", label: "LinkedIn" },
    { icon: FaFacebookF, href: "https://www.facebook.com/profile.php?id=61580082270577", label: "Facebook" },
  ];

  return (
    <footer className="bg-card border-t border-border">
      {/* Newsletter Banner */}
      <div className="border-b border-border bg-muted/30">
        <div className="container py-8 md:py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2">Stay Updated</h3>
              <p className="text-muted-foreground">
                Subscribe to our newsletter for the latest updates and insights.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="flex gap-2 w-full md:w-auto max-w-md">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
                required
              />
              <Button type="submit">
                Subscribe
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2 space-y-4">
            <Link to="/" className="flex items-center space-x-2">
              <img src={logoImg} alt="Oethical Technology" className="h-10 w-auto" />
              <span className="font-bold text-lg">Oethical Technology</span>
            </Link>
            <p className="text-muted-foreground leading-relaxed">
              Empowering businesses with innovative IT solutions. We deliver cutting-edge technology
              services tailored to your needs.
            </p>
            <div className="space-y-3">
              <a
                href="https://maps.google.com/?q=D2/258+Narmadapuram+Rd+Bhopal"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-muted-foreground hover:text-foreground transition-colors"
              >
                <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm">
                  D2/258, Narmadapuram Rd, opposite Aashima Mall, Danish Nagar, Bagmugaliya,
                  Bhopal, Madhya Pradesh 462026
                </span>
              </a>
              <a
                href="tel:+919532912517"
                className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Phone className="h-5 w-5 text-primary" />
                <span className="text-sm">+91 9532912517</span>
              </a>
              <a
                href="mailto:admin@oethical.com"
                className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Mail className="h-5 w-5 text-primary" />
                <span className="text-sm">admin@oethical.com</span>
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Company</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Services</h4>
            <ul className="space-y-2">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Legal</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border bg-muted/30">
        <div className="container py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © {getCurrentYear()}{" "}
              <Link to="/" className="text-primary hover:underline font-semibold">
                Oethical Technology
              </Link>
              . All rights reserved.
            </p>
            <div className="flex gap-4">
              {socialLinks.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  aria-label={label}
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
